import { FC, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Typography,
  Badge,
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Input,
  toast,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconTrash, IconEdit, IconCheck, IconX, IconHistory } from "@tabler/icons-react";
import { LocalAgent, LocalAgentVersion } from "../hooks/useLocalAgents";
import { LocalAgentVersionHistory } from "./LocalAgentVersionHistory";
import { getValidModel } from "../utils";

interface LocalAgentDetailsProps {
  agent: LocalAgent;
  onDelete: () => void;
  onUpdate: (updates: Partial<Omit<LocalAgent, "id" | "created_at" | "isLocal">>) => void;
  availableModels?: string[];
  agentVersions: LocalAgentVersion[];
  onRestoreVersion: (version: number) => void;
}

interface AgentFormData {
  name: string;
  description: string;
  model: string;
  prompt: string;
}

export const LocalAgentDetails: FC<LocalAgentDetailsProps> = ({
  agent,
  onDelete,
  onUpdate,
  availableModels = [],
  agentVersions,
  onRestoreVersion,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const validModel = getValidModel(agent.model, availableModels);

  const { register, handleSubmit, setValue, watch, reset } = useForm<AgentFormData>({
    defaultValues: {
      name: agent.name,
      description: agent.description,
      model: validModel,
      prompt: agent.prompt,
    },
  });

  useEffect(() => {
    reset({
      name: agent.name,
      description: agent.description,
      model: getValidModel(agent.model, availableModels),
      prompt: agent.prompt,
    });
  }, [agent, reset, availableModels]);

  const handleCancel = () => {
    setIsEditing(false);
    reset({
      name: agent.name,
      description: agent.description,
      model: agent.model,
      prompt: agent.prompt,
    });
  };

  const onSubmit = (data: AgentFormData) => {
    onUpdate(data);
    setIsEditing(false);
    toast({ description: "Local agent updated successfully" });
  };

  const currentModel = watch("model");

  return (
    <>
      <Card>
        <CardHeader>
          <Stack direction="row" justify="between" align="center">
            <div>
              <Stack direction="row" gap="sm" align="center">
                <Typography variant="body" weight="medium">
                  {agent.name}
                </Typography>
                {agent.source_type === "langfuse" ? (
                  <>
                    <Badge className="!al-bg-purple-100 !al-text-purple-700">From Langfuse</Badge>
                    <Badge className="!al-bg-blue-100 !al-text-blue-700">Local Edit</Badge>
                  </>
                ) : (
                  <Badge className="!al-bg-blue-100 !al-text-blue-700">Local</Badge>
                )}
              </Stack>
            </div>
            {!isEditing && (
              <Stack direction="row" gap="sm">
                <Button onClick={() => setShowVersionHistory(true)} variant="outline" size="sm">
                  <IconHistory className="al-w-4 al-h-4 al-mr-2" />
                  Version History
                </Button>
                <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                  <IconEdit className="al-w-4 al-h-4 al-mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete agent "${agent.name}"?`))
                      onDelete();
                  }}
                  variant="outline"
                  size="sm"
                  className="al-text-red-600"
                >
                  <IconTrash className="al-w-4 al-h-4 al-mr-2" />
                  Delete
                </Button>
              </Stack>
            )}
          </Stack>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="al-space-y-6">
            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                Agent Name
              </Typography>
              {isEditing ? (
                <Input
                  {...register("name")}
                  className="al-border-gray-300 al-bg-gray-50 focus:al-border-gray-400 focus:al-ring-gray-200"
                />
              ) : (
                <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-3 al-rounded-lg">
                  <Typography variant="body" weight="medium" className="al-text-gray-900">
                    {agent.name}
                  </Typography>
                </div>
              )}
            </div>

            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                Description
              </Typography>
              {isEditing ? (
                <Textarea
                  {...register("description")}
                  rows={3}
                  className="al-border-gray-300 al-bg-gray-50 focus:al-border-gray-400 focus:al-ring-gray-200"
                />
              ) : (
                <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-3 al-rounded-lg">
                  <Typography className="al-text-gray-800">{agent.description}</Typography>
                </div>
              )}
            </div>

            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                Model
              </Typography>
              {isEditing ? (
                <Select value={currentModel} onValueChange={value => setValue("model", value)}>
                  <SelectTrigger className="al-w-full">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model: string) => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="al-bg-blue-50 al-border al-border-blue-200 al-p-3 al-rounded-lg">
                  <Typography className="al-font-mono al-text-blue-900">{validModel}</Typography>
                </div>
              )}
            </div>

            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                Version
              </Typography>
              <Stack direction="row" gap="sm" align="center">
                <Badge className="!al-bg-purple-100 !al-text-purple-700">
                  Current: v{agent.version}
                </Badge>
                {agentVersions && agentVersions.length > 1 && (
                  <Typography className="al-text-gray-500">
                    {agentVersions.length} version{agentVersions.length !== 1 ? "s" : ""} total
                  </Typography>
                )}
              </Stack>
            </div>

            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                Created At
              </Typography>
              <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-3 al-rounded-lg">
                <Typography className="al-text-gray-800">
                  {new Date(agent.created_at).toLocaleString()}
                </Typography>
              </div>
            </div>

            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                System Prompt
              </Typography>
              {isEditing ? (
                <>
                  <Textarea
                    {...register("prompt")}
                    rows={12}
                    className="al-font-mono al-text-sm al-border-blue-200 al-bg-blue-50 focus:al-border-blue-300 focus:al-ring-blue-100"
                  />
                  <Stack direction="row" gap="sm" justify="end" className="al-mt-3">
                    <Button onClick={handleCancel} variant="outline" size="sm" type="button">
                      <IconX className="al-w-4 al-h-4 al-mr-2" />
                      Cancel
                    </Button>
                    <Button type="submit" variant="default" size="sm">
                      <IconCheck className="al-w-4 al-h-4 al-mr-2" />
                      Save
                    </Button>
                  </Stack>
                </>
              ) : (
                <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-4 al-rounded-lg al-max-h-96 al-overflow-y-auto">
                  <pre className="al-text-xs al-whitespace-pre-wrap al-font-mono al-text-gray-800">
                    {agent.prompt}
                  </pre>
                </div>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Version History Dialog */}
      <LocalAgentVersionHistory
        agentName={agent.name}
        currentVersion={agent.version}
        versions={agentVersions}
        open={showVersionHistory}
        onOpenChange={setShowVersionHistory}
        onRestore={onRestoreVersion}
      />
    </>
  );
};
